/**
 * Treatment variant for A/B test: "Increase Perceived PK Sub Value"
 * Test ID: money_increase-perceived-pk-sub-value
 * LaunchDarkly: https://app.launchdarkly.com/projects/default/flags/money_increase-perceived-pk-sub-value
 * Ticket: https://lovevery.atlassian.net/browse/MONEY-1511
 * Owner: Felipe Moslavacz
 */
import React, { FC, useCallback, useState, useEffect } from 'react';
import {
  TypeImage,
  TypeImageFields,
  TypeMediaTextTitle,
  TypeProduct,
  TypeVideo,
  TypeVideoEmbedFields,
  TypeVideoFields,
  TypeTabNavigation,
} from '@/core/generated-types';
import { useSubFlowV3Data } from '@/components/subflow-v3/context/SubFlowV3Context';
import { Loader, RichText, DynamicComponent } from '@/components';
import styles from './PlayKitDetailsHeaderTreatment.module.scss';
import Heading, {
  HeadingTag,
  HeadingSize,
} from '@/components/ui/Heading/Heading';
import { Document } from '@contentful/rich-text-types';
import { shouldUseYotpoReviews } from '@/utils/helpers';
import { YotpoReviewStars } from '@/components/ui/YotpoReviewStars';
import { CarouselProducts } from '@/components/common/Carousel';
import { Entry, EntryFields } from 'contentful';
import { TariffPDPMessage } from '@/components/common/Tariff';
import { TypeTariffSettings } from '@/components/common/types';
import { ShopifyProduct, ShopifyVariant } from '@/lib/shopify';
import { ProductVariantCopy } from '@/lib/contentful';
import useLocale from '@/hooks/useLocale';
import useFeature from '@/hooks/useFeature';
import PromoSlotPdp from '@/components/promoSlot/PromoSlotPdp';
import { createStandaloneProduct, createSubscriptionProduct } from './utils';
import { useInventoryThreshold } from '@/hooks/useInventoryThreshold';
import { TabNavigation } from '@/components/common/TabNavigation';
import { PDPPromoText } from '@/components/common/PDPPromoText';
import { createTabs, NoTabsContent } from './PlayKitTabsContent';
import { getDefaultVariant } from '@/utils/shopify';
import { SelectedAddOns } from '@/components/common/ProductAddToCart/ProductAddToCart';
import { addToCart } from '@/lib/cart.browser';
import { CartItemProperty, ITEM_ORIGINS } from '@/core/constants';
import { trackAddOnEvents } from './utils';
import classNames from 'classnames';
import { buildCartUrl } from '@/lib/cart/helpers';
interface PlayKitDetailsHeaderTreatmentProps {
  product: TypeProduct;
  productDescription?: EntryFields.RichText;
  carouselProductsComponents:
    | (TypeMediaTextTitle | TypeVideo | TypeImage)[]
    | Entry<TypeImageFields | TypeVideoFields | TypeVideoEmbedFields>[];
  pathname: string;
  handleClick: (productSelected?: TypeProduct) => void;
  tariffSettings?: TypeTariffSettings;
  enableIndividualPlayKitParts?: boolean;
  tabsComponent?: TypeTabNavigation;
  bottomMarkerRef: React.RefObject<HTMLDivElement>;
  subscribeCtaMessage?: EntryFields.RichText;
  productVariantsCopy: ProductVariantCopy | null;
  isBfcmEnabled?: boolean;
  evergreenPromoText?: EntryFields.RichText;
}

interface PlayKitBodyInterface {
  aboutHeading?: string;
  shipMessage?: string;
  optionDetails?: EntryFields.RichText;
}

// Value to be used for the standalone product threshold - if the inventory is below this value, the button will be shown as out of stock
export const STANDALONE_THRESHOLD = 10;

export const PlayKitDetailsBodyTreatment: FC<PlayKitBodyInterface> = ({
  aboutHeading,
  shipMessage,
  optionDetails,
}) => {
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <Heading
          tag={HeadingTag.H3}
          size={HeadingSize.H3}
          text={aboutHeading}
        />
        {shipMessage && (
          <h3
            className="font-bold text-charcoal text-h-x5 md:text-h-x4"
            role="heading"
            dangerouslySetInnerHTML={{ __html: shipMessage }}
          />
        )}
      </div>
      <PromoSlotPdp className="mb-4" slotId="pdp-play-kit-details" isPlayKit />
      {optionDetails && (
        <div className={styles.optionDetails}>
          <RichText
            content={optionDetails as Document}
            addParagraphMargins={false}
          />
        </div>
      )}
    </>
  );
};

export const PlayKitDetailsHeaderTreatment: FC<
  PlayKitDetailsHeaderTreatmentProps
> = ({
  product,
  productDescription,
  carouselProductsComponents,
  pathname,
  handleClick,
  tariffSettings,
  enableIndividualPlayKitParts,
  tabsComponent,
  bottomMarkerRef,
  subscribeCtaMessage,
  productVariantsCopy,
  isBfcmEnabled = false,
  evergreenPromoText,
}) => {
  const {
    sys: { id },
  } = product;
  const locale = useLocale();
  const {
    api: { updatePurchaseOption },
  } = useSubFlowV3Data();
  const { title, shipMessage, cta, ageDescription } = product.fields;
  const [productSelected, setProductSelected] = useState(product);
  const [isStandaloneSelected, setIsStandaloneSelected] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState<SelectedAddOns[]>([]);
  const isSubscribeAndSave = useFeature('money_enable-subscribe-and-save');
  const isEarlyDecSubCampaignEnabled = useFeature(
    'money_early-dec-sub-campaign',
  );
  const isIndividualPlaythingsEnabled = useFeature(
    'money_enable-individual-playthings',
  );
  const isResaleValuePdpEnabled = useFeature(
    'money_resale-value-on-plk-pdp-v2',
  );

  useEffect(() => {
    updatePurchaseOption(product);
  }, [product, updatePurchaseOption]);

  const handleOptionSelected = useCallback(
    (
      product: TypeProduct,
      _shopifyProduct: ShopifyProduct,
      _variant: ShopifyVariant,
    ) => {
      setProductSelected(product);
      updatePurchaseOption(product);
      setIsStandaloneSelected(product.sys.id.includes('standalone'));
    },
    [updatePurchaseOption],
  );

  const handleAddToCart = async () => {
    try {
      const cartItems = selectedAddOns.map((selectedAddOn) => ({
        merchandiseId: selectedAddOn.variant.id,
        quantity: 1,
        attributes: [
          {
            key: CartItemProperty.PriceSource,
            value: 'standalone',
          },
          {
            key: CartItemProperty.Source,
            value: 'product_page',
          },
          { key: CartItemProperty.QuantityLimitedToOne, value: 'false' },
          {
            key: CartItemProperty.ItemOrigin,
            value: ITEM_ORIGINS.PRODUCT_PAGE,
          },
          {
            key: 'display_price',
            value: selectedAddOn.variant.priceCentsStandalone?.toString() || '',
          },
        ],
      }));

      const response = await addToCart(cartItems, 1, { noredirect: 1 });

      if (!response.ok) {
        throw new Error('addToCart failed to execute');
      }

      await trackAddOnEvents(selectedAddOns);

      const cartUrl = buildCartUrl(locale);
      window.location.href = cartUrl;
    } catch (error) {
      console.error('Error adding to cart', error);
    }
  };

  const subscriptionProduct = createSubscriptionProduct(product);
  const standaloneProduct = createStandaloneProduct(product, locale);
  const subscriptionShopifyProduct = subscriptionProduct.fields.shopifyProduct;
  const standaloneShopifyProduct = standaloneProduct?.fields.shopifyProduct;
  const isStandaloneEnabled =
    isSubscribeAndSave?.toString().toLowerCase() === 'enabled' &&
    standaloneProduct &&
    standaloneShopifyProduct;

  const { inventoryData, isLoading } = useInventoryThreshold({
    productId: standaloneShopifyProduct?.id,
    threshold: STANDALONE_THRESHOLD,
    skip: !isStandaloneEnabled,
  });

  const shouldShowPlaythingTabs =
    isIndividualPlaythingsEnabled &&
    enableIndividualPlayKitParts &&
    productSelected?.fields?.productAddOns?.length &&
    productSelected?.fields?.productAddOns?.length > 0;

  const currentProduct = isStandaloneEnabled ? productSelected : product;

  if (!subscriptionShopifyProduct) {
    return (
      <div className="lg:flex lg:gap-6">
        <div className="pb-3 -m-6 md:mx-0 lg:p-0 lg:m-0 lg:basis-7/12">
          <CarouselProducts
            name="pdp hero carousel"
            id={id}
            components={carouselProductsComponents}
            key={pathname}
          />
        </div>
        <div className="mt-10 lg:mt-0 lg:basis-5/12">
          <Loader loading={true} />
        </div>
      </div>
    );
  }
  const shopifyProduct = productSelected.fields.shopifyProduct;
  const variants = shopifyProduct?.variants || [];
  const defaultVariant = getDefaultVariant(
    productSelected.fields.title,
    variants,
  );
  const showPromotionBadge = isBfcmEnabled || isEarlyDecSubCampaignEnabled;

  return (
    <div className="lg:flex lg:gap-6">
      <div
        className={classNames('pb-3 -m-6 md:mx-0 lg:p-0 lg:m-0 lg:basis-7/12', {
          'lg:h-fit lg:sticky lg:top-20': shouldShowPlaythingTabs,
        })}
      >
        <CarouselProducts
          name="pdp hero carousel"
          id={id}
          components={carouselProductsComponents}
          key={pathname}
        />
      </div>
      <div className="mt-10 lg:mt-0 lg:basis-5/12">
        {showPromotionBadge && product.fields.badge?.fields.text && (
          <div className="mb-2">
            <DynamicComponent component={product.fields.badge} />
          </div>
        )}
        <p className="text-base font-bold md:text-lg">{ageDescription}</p>
        <Heading tag={HeadingTag.H1} size={HeadingSize.H1} text={title} />
        {shouldUseYotpoReviews() && (
          <div className="my-3 md:mb-3">
            <YotpoReviewStars product={product} />
          </div>
        )}
        {productDescription && (
          <div className="text-base md:text-lg mb-4">
            <RichText content={productDescription as Document} />
          </div>
        )}

        {isSubscribeAndSave === undefined ||
          (isIndividualPlaythingsEnabled === undefined && (
            <Loader loading={true} />
          ))}

        {shouldShowPlaythingTabs ? (
          <TabNavigation
            title={tabsComponent?.fields?.title || 'Ways to buy:'}
            eventPageLocation="ways_to_buy"
            titleClassName="text-h-x4 font-bold self-start pb-3"
            showSkeletonOnLoad={false}
            tabs={createTabs({
              isStandaloneEnabled: Boolean(isStandaloneEnabled),
              subscriptionProduct,
              standaloneProduct,
              subscriptionShopifyProduct,
              standaloneShopifyProduct,
              productSelected,
              handleOptionSelected,
              product,
              isStandaloneSelected,
              inventoryData,
              isLoading,
              handleClick,
              shipMessage,
              cta,
              tabsComponent,
              selectedAddOns,
              setSelectedAddOns,
              defaultVariant,
              bottomMarkerRef,
              handleAddToCart,
              subscribeCtaMessage,
              productVariantsCopy,
            })}
            tabType={tabsComponent?.fields?.tabType || 'pill'}
          />
        ) : (
          <NoTabsContent
            isStandaloneEnabled={Boolean(isStandaloneEnabled)}
            isResaleValuePdpEnabled={isResaleValuePdpEnabled === 'treatment'}
            subscriptionProduct={subscriptionProduct}
            standaloneProduct={standaloneProduct}
            subscriptionShopifyProduct={subscriptionShopifyProduct}
            standaloneShopifyProduct={standaloneShopifyProduct}
            productSelected={productSelected}
            handleOptionSelected={handleOptionSelected}
            product={product}
            isStandaloneSelected={isStandaloneSelected}
            inventoryData={inventoryData}
            isLoading={isLoading}
            handleClick={handleClick}
            shipMessage={shipMessage}
            cta={cta}
            productVariantsCopy={productVariantsCopy}
            isBfcmEnabled={isBfcmEnabled}
          />
        )}

        {evergreenPromoText && (
          <PDPPromoText content={evergreenPromoText as Document} />
        )}

        <TariffPDPMessage
          product={currentProduct}
          tariffSettings={tariffSettings}
        />
      </div>
    </div>
  );
};
